import { CrmJobConfig } from "../hooks/types";

/**
 * Type guard function to check if an object is a valid CrmJobConfig
 * @param value - The value to check
 * @returns True if the value is a valid CrmJobConfig, false otherwise
 */
export const isCrmJobConfig = (value: unknown): value is CrmJobConfig => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  // Check if tools property exists and validate it
  if ("tools" in obj) {
    if (!Array.isArray(obj.tools)) {
      return false;
    }

    // Validate each tool in the array
    return obj.tools.every((tool) => {
      if (typeof tool !== "object" || tool === null || Array.isArray(tool)) {
        return false;
      }

      const toolObj = tool as Record<string, unknown>;

      // Check required properties
      if (typeof toolObj.tool_id !== "string" || typeof toolObj.tool_type !== "string") {
        return false;
      }

      // Check options if they exist
      if ("options" in toolObj) {
        if (!Array.isArray(toolObj.options)) {
          return false;
        }

        return toolObj.options.every((option) => {
          if (typeof option !== "object" || option === null || Array.isArray(option)) {
            return false;
          }

          const optionObj = option as Record<string, unknown>;
          return typeof optionObj.key === "string" && typeof optionObj.value === "string";
        });
      }

      return true;
    });
  }

  // If no tools property, it's still a valid empty CrmJobConfig
  return true;
};

/**
 * Function to safely parse and validate CrmJobConfig from JSON string
 * @param jsonString - The JSON string to parse
 * @returns A valid CrmJobConfig object, or empty config if parsing/validation fails
 */
export const parseCrmJobConfig = (jsonString: string): CrmJobConfig => {
  try {
    const parsed: unknown = JSON.parse(jsonString);

    if (isCrmJobConfig(parsed)) {
      return parsed;
    }

    return {}; // Return empty config if validation fails
  } catch {
    return {}; // Return empty config if JSON parsing fails
  }
};
